import clsx from 'clsx';
import { Spinner } from './Spinner';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  className,
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles =
    'inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors duration-150 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-text-primary disabled:opacity-40 disabled:cursor-not-allowed';

  const variants = {
    primary:
      'bg-surface-inverse text-text-inverse hover:bg-[#2a2a2a] active:bg-text-primary',
    secondary:
      'bg-surface-primary text-text-primary border border-border hover:bg-surface-secondary active:bg-surface-tertiary',
    outline:
      'border border-border bg-transparent text-text-primary hover:bg-surface-secondary active:bg-surface-tertiary',
    ghost:
      'bg-transparent text-text-secondary hover:bg-surface-tertiary hover:text-text-primary active:bg-surface-sunken',
    danger:
      'bg-surface-primary text-error-dark border border-error/30 hover:bg-error-light active:bg-error-light',
  };

  const sizes = {
    sm: 'h-8 px-3 text-body-sm gap-1.5',
    md: 'h-9 px-4 text-body-sm gap-2',
    lg: 'h-11 px-5 text-body-md gap-2',
  };

  return (
    <button
      className={clsx(baseStyles, variants[variant], sizes[size], className)}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <Spinner size="sm" />
      ) : leftIcon ? (
        <span className="flex-shrink-0">{leftIcon}</span>
      ) : null}
      {children}
      {rightIcon && !isLoading && <span className="flex-shrink-0">{rightIcon}</span>}
    </button>
  );
}
